import Vue from 'vue'
import App from './App.vue'
import Venue from './models/Venue'
import Prompt from './models/Prompt'
import Booking from './models/Booking'
import Modal from './models/Modal'
import { Source } from './models/Source';
import DateService from './services/DateService'
import BookingType from './models/BookingType';
import { EventEmitter } from 'events'
import VueI18n from 'vue-i18n';

class Widget extends EventEmitter {
    private readonly vue: any
    private readonly mainComponent: any

    private events: string[] = [
        'finishedLoading',
        'setVenue',
        'setDateNumberPeople',
        'setBookingType',
        'setTime'
    ]

    constructor (vue: Vue) {
        super()
        this.vue = vue
        this.mainComponent = vue.$root.$children[0]
        this.registerEvents();
    }

    registerEvents () {
        this.events.forEach((event) => {
            this.vue.$on(event, (e: any) => {
                this.emit(event, this.vue.$root.booking)
            })
        })
    }

    /**
     * An object containing the user defined options for this widget.
     * @return {Object}
     */
    get options() {
        return this.vue.$root.dmnOptions
    }

    set options (options) {
        this.vue.$root.dmnOptions = options
    }

    showOffers (): this {
        this.options.hideOffers = false

        return this
    }

    hideOffers (): this {
        this.options.hideOffers = true

        return this
    }

    showVenueSearch (): this {
        this.options.searchVenues = true

        return this
    }

    hideVenueSearch (): this {
        this.options.searchVenues = false

        return this
    }

    setSelectableBookingTypesById(bookingTypeIds: string[]): this {
        this.options.allowedTypes = bookingTypeIds;

        return this;
    }

    setMinGuests(guests: number): this {
        const paxSelector = this.mainComponent.$refs.paxSelector;

        paxSelector.setMin(guests);
        paxSelector.options = paxSelector.buildOptions();
        paxSelector.ensureValueFitsRange();
        paxSelector.rangeManuallySet = true;

        return this;
    }

    setMaxGuests(guests: number): this {
        const paxSelector = this.mainComponent.$refs.paxSelector;

        paxSelector.setMax(guests);
        paxSelector.options = paxSelector.buildOptions();
        paxSelector.ensureValueFitsRange();
        paxSelector.rangeManuallySet = true;

        return this;
    }

    /**
     * Returns an array of venues which the user can select from. This is
     * retrieved from the venue IDs passed in as an option
     * @return {Venue[]}
     */
    getSelectableVenues (): Venue[] {
        return this.vue.$root._venues
    }

    /**
     * Returns an array of the booking types which the user can select from.
     * @return {BookingType[]}
     */
    getSelectableTypes (): BookingType[] {
        return this.mainComponent.$refs.typeStage.types
    }

    /**
     * Sets the venues which a user can select from on the initial stage.
     * @param {string[]} venues An array of venue IDs as a string
     * @return {this}
     */
    setSelectableVenuesById (venueIds: string[]): this {
        this.options.venue = venueIds

        return this
    }

    /**
     * Sets the selected venue the booking will be made for. A venue object
     * must be passed to this method.
     * @see getSelectableVenueById
     * @param {Venue} venue A ven
     */
    setVenue (venue: Venue): this {
        this.vue.$root.booking.venue = venue
        this.mainComponent.setStageTextForVenue()
        this.goToDateAndGuestSelectionStage()

        return this
    }

    /**
     * Sets the selected venue the booking will be made for, by specifying
     * the ID of the venue. The venue must have been included in the
     * selectableVenues.
     * @param {string} venueId The ID of the venue to select
     * @return {this}
     */
    setVenueById (venueId: string): this {
        const venue = this.getSelectableVenueById(venueId)

        if (venue) {
            this.setVenue(venue)
        }

        return this
    }

    /**
     * Returns a Venue object for the specified venue ID. Note: this will only
     * return a venue if a match was found in the selectable venues
     * @param {string} venueId The ID of the venue to get
     * @return {Venue | null} A venue object, or null if none found
     */
    getSelectableVenueById (venueId: string): Venue | null {
        const results = this.getSelectableVenues().filter((venue) => {
            return venue.id === venueId
        })

        return results.length ? results[0] : null
    }

    /**
     * Returns a BookingType object for the specified booking type ID.
     * @param {BookingType} typeId
     * @return {BookingType | null}
     */
    getSelectableTypeById (typeId: string): BookingType | null {
        const results = this.getSelectableTypes().filter((type) => {
            return type.id === typeId
        })

        return results.length ? results[0] : null
    }

    /**
     * Sets the number of guests for this booking.
     * @param {number} guests Number of guests
     * @return {this}
     */
    setNumberOfGuests (guests: number): this {
        this.vue.$root.booking.pax = guests
        this.mainComponent.setStageTextForPeopleAndDate()

        return this
    }

    /**
     * Sets the date for this booking.
     * @param {Date} date The date of the booking. Time is ignored here
     * @return {this}
     */
    setDate (date: Date): this {
        this.vue.$root.booking.date = DateService.getStartOfDay(date)
        this.mainComponent.setStageTextForPeopleAndDate()
        this.mainComponent.$refs.dateStage.setDate(date)

        return this
    }

    /**
     * Moves the calendar to the specified month.
     * @param {Date} date A date containing the month to move to
     * @return {this}
     */
    goToMonth (date: Date): this {
        const month = DateService.getStartOfMonth(date)

        this.mainComponent.$refs.dateStage.moveToMonth(month)

        return this
    }

    setType (type: BookingType): this {
        this.vue.$root.booking.type = type
        this.mainComponent.setStageTextForType()
        this.goToTimeSelectionStage()

        return this
    }

    setTypeById (typeId: string): this {
        const type = this.getSelectableTypeById(typeId);

        if (type) {
            this.setType(type);
        }

        return this
    }

    setStartTime (startTime: string): this {
        return this
    }

    setDuration (duration: number): this {
        return this
    }

    setEndTime (endTime: number): this {
        return this
    }

    setEmailAddress (email: string): this {
        this.vue.$root.booking.user.email = email

        return this
    }

    setFirstName (firstName: string): this {
        this.vue.$root.booking.user.firstName = firstName

        return this
    }

    setLastName (lastName: string): this {
        this.vue.$root.booking.user.lastName = lastName

        return this
    }

    setDateOfBirth (date: number, month: number, year: number): this {
        return this
    }

    setMobileNumber (number: string): this {
        this.vue.$root.booking.user.phone = number

        return this
    }

    setNotes (specialRequest: string): this {
        this.vue.$root.booking.specialRequest = specialRequest

        return this
    }

    goToVenueStage (): this {
        this.mainComponent.goToVenueStage()

        return this
    }

    goToDateAndGuestSelectionStage (): this {
        this.mainComponent.goToDateStage()

        return this
    }

    goToTypeSelectionStage (): this {
        this.mainComponent.goToTypeStage()

        return this
    }

    goToTimeSelectionStage (): this {
        this.mainComponent.goToTimeStage()

        return this
    }

    goToDetailsStage (): this {
        this.mainComponent.goToDetailsStage()

        return this
    }

    goToAlternativeAvailabilityStage (): this {
        return this
    }
}

export class TagOptions {

    ref: String | null = null
    locale: string = 'en-GB'
    venue: string[] = []
    searchVenues: Boolean = false
    from: Source = Source.partner
    hidePoweredBy: Boolean = false
    showTypeFirst: Boolean = false
    defer: Boolean = false
    hideOffers: Boolean = false
    affiliateId: String | null = null
    mondayFirst: Boolean = true
    customSource: String | null = null
    allowedTypes: string[] = []
    returnUrl: string = ''
    googleAnalyticsCode: string | null = null
    googleTagManagerCode: string | null = null
    stylesheet: string | null = null
    linkedBookingsEnabled: Boolean = false

    constructor (data: any) {
        let attrName: string

        for (let i = 0; i < data.length; i++) {
            attrName = this.strToCamelCase(data[i].name)

            switch (attrName) {
                case "ref":
                    this.ref = data[i].value
                    break
                case "locale":
                    // Supported locales are en-GB or sv. Use en-GB for unknown.
                    const supportedLocales = ['en-GB', 'sv']

                    if (supportedLocales.indexOf(data[i].value) === -1) {
                        this.locale = 'en-GB'
                    } else {
                        this.locale = data[i].value
                    }
                    break
                case "linkedBookings":
                    this.linkedBookingsEnabled = data[i].value !== 'false'
                    break
                case "venue":
                    this.venue = data[i].value.split(',')
                    break
                case "searchVenues":
                    this.searchVenues = data[i].value !== 'false'
                    break
                case "from":
                    this.from = data[i].value
                    break
                case "hidePoweredBy":
                    this.hidePoweredBy = data[i].value !== 'false'
                    break
                case "showTypeFirst":
                    this.showTypeFirst = data[i].value !== 'false'
                    break
                case "defer":
                    this.defer = data[i].value !== 'false'
                    break
                case "hideOffers":
                    this.hideOffers = data[i].value !== 'false'
                    break
                case "affiliateId":
                    this.affiliateId = data[i].value
                    break
                case "mondayFirst":
                    this.mondayFirst = data[i].value !== 'false'
                    break
                case "customSource":
                    this.customSource = data[i].value
                    break
                case "allowedTypes":
                    this.allowedTypes = data[i].value.split(',').filter(Boolean)
                    break
                case "returnUrl":
                    this.returnUrl = data[i].value
                    break
                case "googleAnalyticsCode":
                    this.googleAnalyticsCode = data[i].value
                    break
                case "googleTagManagerCode":
                    this.googleTagManagerCode = data[i].value
                    break
                case "stylesheet":
                    this.stylesheet = data[i].value
            }
        }
    }

    get showOffers (): boolean {
        return !this.hideOffers
    }

    private strToKebabCase (str: string): string {
        const regex = new RegExp('([a-z])([A-Z])', 'g')

        return str.replace(regex, '$1-$2').toLowerCase()
    }

    private strToCamelCase (str: string): string {
        const regex = new RegExp('-([a-z])', 'g')

        return str.replace(regex, start => start[1].toUpperCase())
    }

    static readonly requiredFields: string[] = [
        'venue'
    ]
}

export default class BookingFormHelper {
    widgets: Widget[] = []

    /**
     * Finds script tags on the page. Goes through each one, ensures it has the
     * required attributes, and that it hasn't already been rendered. Then
     * renders it.
     * @return {Widget[]}
     */
    public findBookingFormScriptTags (): Widget[] {
        const scriptTags = document.getElementsByTagName('script')

        for (let i = 0; i < scriptTags.length; i++) {
            this.renderBookingWidget(scriptTags[i])
        }

        return this.widgets
    }

    /**
     * Renders the specified HTMLScriptElement tag as a Booking Form Widget
     * and adds it to the array of widgets on this page
     * @param {HTMLScriptElement} tag
     * @return {void}
     */
    public renderBookingWidget (tag: HTMLScriptElement): Widget | null {
        if (this.isValidTag(tag) && !this.isTagRendered(tag)) {
            const options = this.extractPropertiesFromTag(tag)
            const vue = this.bootVueForTag(tag, options)
            const widget = new Widget(vue)

            this.setTagAsRendered(tag)
                .addWidgetToArray(widget)

            return widget
        }

        return null
    }

    /**
     * Returns a widget by the reference specified as the ref attribute
     * on the script tag, or null if it was not found.
     * @param {String} ref
     * @return {Widget | null}
     */
    public getWidgetByRef (ref: String): Widget | null {
        let widgetToReturn = null

        this.widgets.forEach((widget) => {
            if (widget.options.ref === ref) {
                widgetToReturn = widget
                return
            }
        })

        return widgetToReturn
    }

    /**
     * Determines whether or not the specified tag is a valid booking form
     * script tag by checking if it has all the required attributes
     * @param {HTMLScriptElement} tag
     * @return {Boolean}
     */
    public isValidTag (tag: HTMLScriptElement): boolean {
        let valid = true

        TagOptions.requiredFields.forEach((field) => {
            if (!tag.hasAttribute(field)) {
                valid = false
            }
        })

        valid = this.isAllowedSource(tag)

        return valid
    }

    /**
     * Determines whether or not the specified tag has a trusted source
     * @param {HTMLScriptElement} tag
     * @return {Boolean}
     */
    private isAllowedSource (tag: HTMLScriptElement): boolean {
        let valid: boolean = false
        const allowedSources: RegExp[] = [
            /^(\/\/widgets.designmynight\.com\/bookings)(.+)?(\.js)$/,
            /^\/app.js$/
        ]

        const src = tag.getAttribute('src')

        allowedSources.forEach((regex) => {
            if (regex.test(src!)) {
                valid = true
            }
        })

        return valid
    }

    /**
     * Returns whether or not the specified script tag has been rendered already
     * @param {HTMLScriptElement} tag
     * @return {Boolean}
     */
    public isTagRendered (tag: HTMLScriptElement): boolean {
        return tag.getAttribute('data-rendered') === 'true'
    }

    /**
     * Extracts the properties of the script tag and returns a TagOptions object
     * @param {HTMLScriptElement} tag
     * @return {TagOptions}
     */
    private extractPropertiesFromTag (tag: HTMLScriptElement): TagOptions {
        return new TagOptions(tag.attributes)
    }

    /**
     * Internally sets the specified script tag as already rendered, so that it
     * won't get rendered again
     * @param {HTMLScriptElement} tag
     * @return {this}
     */
    private setTagAsRendered (tag: HTMLScriptElement): this {
        tag.setAttribute('data-rendered', 'true')

        return this
    }

    /**
     * Instantiates a new instance of VueJS for the specified script tag,
     * passing in all the specified options
     * @param {HTMLScriptElement} tag
     * @return {Vue}
     */
    private bootVueForTag(tag: HTMLScriptElement, options: TagOptions): Vue {
        const translations = {
            'en-GB': {
                global: {
                    edit: 'Edit',
                    back: 'Back',
                    change: 'Change',
                },
                venue_stage: {
                    header: 'Select a venue',
                },
                date_and_pax_stage: {
                    header: 'Select group number & date below',
                    number_suffix: 'guest | guests',
                    today: 'Today',
                    tomorrow: 'Tomorrow',
                    next_available_date: 'The next available booking date is ',
                    jump_to_next_available: 'Jump to month',
                },
                booking_type_stage: {
                    header: 'Select booking type below'
                },
                time_stage: {
                    header: 'Select your timings below',
                    start_time: 'Start Time',
                    end_time: 'End Time',
                    start_time_prompt: 'Select your start time first',
                    end_time_prompt: 'Now select your end time',
                    until_close: 'Until Close',
                    no_availability: 'No availability',
                    see_alternatives: 'See alternatives',
                    check_alternative_venues: 'Check alternative venues',
                    no_availability_message_for_time: 'There\'s no availability for {type} at {time}',
                    no_availability_message_for_date: 'There\'s no availability for {type} at {venue} on {date}',
                    instant_book: 'Instant Book',
                    make_enquiry: 'Make Enquiry',
                    time_unavailable: 'Unavailable',
                },
                offers_stage: {
                    every_day: 'Every Day',
                    weekends: 'Weekends',
                    weekdays: 'Weekdays',
                    min: 'Min',
                    max: 'Max',
                    valid_from: 'Valid from',
                    valid_to: 'Valid to',
                    see_offers: 'See Offers',
                    hide_offers: 'Hide Offers',
                    offers_available: 'offer available | offers available'
                }
            },
            'sv': {
                global: {
                    edit: 'Ändra',
                    back: 'Ändra',
                    change: 'Ändra',
                },
                venue_stage: {
                    header: 'Välj ställe',
                },
                date_and_pax_stage: {
                    header: 'Välj grupp nummer och datum nedan',
                    number_suffix: 'gäst | gäster',
                    today: 'Idag',
                    tomorrow: 'Imorgon',
                    next_available_date: 'Nästa tillgängliga bokningsdatum är {date}',
                    jump_to_next_available: 'Hoppa till nästa tillgängliga datum'
                },
                booking_type_stage: {
                    header: 'Välj bokningstid nedan'
                },
                time_stage: {
                    header: 'Välj tider nedan',
                    start_time: 'Starttid',
                    end_time: 'Sluttid',
                    start_time_prompt: 'Välj din starttid först',
                    end_time_prompt: 'Välj din sluttid nu',
                    until_close: 'Fram till stängningstid',
                    no_availability: 'Ingen tillgänglighet',
                    see_alternatives: 'Se alternativ',
                    check_alternative_venues: 'Se alternativa ställen',
                    no_availability_message_for_time: 'Det finns ingen tillgänglighet för {type} klockan {time}',
                    no_availability_message_for_date: 'Det finns ingen tillgänglighet för {type} hos {venue} den {date}',
                    instant_book: 'Boka direkt',
                    make_enquiry: 'Gör förfrågan',
                    time_unavailable: 'Ingen tillgänglighet',
                },
                offers_stage: {
                    every_day: 'Varje dag',
                    weekends: 'Helger',
                    weekdays: 'Veckodagar',
                    min: 'Min',
                    max: 'Max',
                    valid_from: 'Valid from',
                    valid_to: 'Giltig till',
                    see_offers: 'Visa erbjudanden',
                    hide_offers: 'Göm erbjudanden',
                    offers_available: 'tillgängligt erbjudande | tillgängligt erbjudanden'
                }
            }
        };

        Vue.use(VueI18n)

        const i18n = new VueI18n({
            locale: options.locale,
            messages: translations,
        })

        return new Vue({
            i18n,
            el: tag,
            render: boot => boot(App),
            data: {
                dmnOptions: options,
                booking: new Booking(),
                prompt: new Prompt(),
                modal: new Modal(),
                confirmedBooking: null,
                _venues: []
            }
        })
    }

    private addWidgetToArray (widget: Widget): this {
        this.widgets.push(widget)

        return this
    }


}



// WEBPACK FOOTER //
// ./src/BookingFormHelper.ts